<template>
  <div
    ref="dropdownRootRef"
    class="v3custom-dropdown"
    tabindex="-1"
    role="group"
    :aria-labelledby="triggerId"
  >
    <div
      class="custom-drop-down"
      :class="{ 'no-bottom-border': state.isVisible }"
    >
      <button
        :id="triggerId"
        type="button"
        class="toggle"
        :class="{ 'is-open': state.isVisible }"
        :aria-expanded="state.isVisible"
        aria-haspopup="listbox"
        :aria-controls="listboxId"
        :aria-label="buttonAriaLabel"
        @click="toggleDropdown"
        @keydown="onTriggerKeydown"
      >
        <span>{{ selectedLabel || label }}</span>
        <i aria-hidden="true" :class="iconClass" />
      </button>
    </div>
    <div
      v-if="options"
      v-show="state.isVisible"
      :id="listboxId"
      class="dropdown-menu"
      :class="{ 'd-block no-top-border': state.isVisible }"
      role="listbox"
      :aria-labelledby="triggerId"
      :aria-activedescendant="listboxActiveDescendantId"
      tabindex="-1"
      @keydown="onListboxKeydown"
    >
      <div
        v-for="(item, index) in options"
        :id="`${listboxId}-opt-${index}`"
        :key="index"
        role="option"
        :aria-selected="isItemSelected(item)"
        :class="{
          active: isItemSelected(item),
          'is-highlighted': state.highlightIndex === index
        }"
        class="dropdown-item"
        @click="selectActive(item)"
        @mousemove="state.highlightIndex = index"
      >
        <span class="dropdown-item-check" aria-hidden="true">
          <i v-if="isItemSelected(item)" class="ri-check-line" />
        </span>
        <span class="dropdown-item-label">{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, onMounted, onUnmounted, computed, nextTick, ref } from "vue"

let idSeq = 0

export default {
  props: {
    options: {
      type: Array,
      default: () => []
    },
    selectedLabel: String,
    selectedItems: {
      type: Array,
      default: () => []
    },
    /**
     * Optional accessible name prefix. When omitted, the button has no `aria-label`
     * and the name comes from visible text (localizable via template/caller).
     */
    ariaLabel: {
      type: String,
      default: ""
    }
  },
  emits: ["itemClick"],
  setup(props, { emit }) {
    const dropdownRootRef = ref(null)
    const instanceId = `cdd-${++idSeq}`
    const triggerId = `${instanceId}-trigger`
    const listboxId = `${instanceId}-listbox`

    const state = reactive({
      isVisible: false,
      selectedOption: null,
      highlightIndex: 0
    })

    const label = computed(() => {
      return state.selectedOption ? state.selectedOption.label : ""
    })

    const iconClass = computed(() => {
      return state.isVisible ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
    })

    const buttonAriaLabel = computed(() => {
      const base = props.ariaLabel?.trim()
      if (!base) return undefined

      const current = (props.selectedLabel || label.value || "").trim()
      return current ? `${base}, ${current}` : base
    })

    /** IDs option rows for aria-activedescendant on the focused listbox (keyboard highlight). */
    const listboxActiveDescendantId = computed(() => {
      if (!state.isVisible || !props.options?.length) return undefined
      const i = state.highlightIndex
      const max = props.options.length - 1
      if (i < 0 || i > max) return undefined
      return `${listboxId}-opt-${i}`
    })

    const isItemSelected = (item) => {
      // Prefer value-based equality when both sides expose a stable `value`,
      // but fall back to reference/label/primitive matching for compatibility
      // with callers that pass primitives or objects without `value`.
      if (!props.selectedItems?.length) {
        return item?.label === label.value
      }

      return props.selectedItems.some((s) => {
        // Both have a `value` property: prefer value-based comparison
        if (
          s &&
          item &&
          typeof s === "object" &&
          typeof item === "object" &&
          "value" in s &&
          "value" in item
        ) {
          return s.value === item.value
        }

        // Fallback 1: reference equality (original includes(item) semantics)
        if (s === item) {
          return true
        }

        // Fallback 2: primitive / label-based comparison
        const sIsPrimitive =
          typeof s === "string" ||
          typeof s === "number" ||
          typeof s === "boolean"
        if (sIsPrimitive && item && typeof item === "object") {
          if ("value" in item && item.value === s) return true
          if ("label" in item && item.label === s) return true
        }

        return false
      })
    }

    const focusDropdownRoot = (attempt = 0) => {
      nextTick(() => {
        const dropdownRootElement = dropdownRootRef?.value
        if (
          !dropdownRootElement ||
          typeof dropdownRootElement.focus !== "function"
        ) {
          return
        }

        dropdownRootElement.focus()

        const shouldRetry =
          (!document.activeElement ||
            document.activeElement === document.body ||
            document.activeElement !== dropdownRootElement) &&
          attempt < 3
        if (shouldRetry) {
          window.setTimeout(() => {
            focusDropdownRoot(attempt + 1)
          }, 40)
        }
      })
    }

    const isElementFocusable = (element) => {
      if (!element || typeof element.focus !== "function") return false
      if (element.hasAttribute("disabled")) return false
      if (element.getAttribute("aria-hidden") === "true") return false
      if (element.getAttribute("tabindex") === "-1") return false
      return element.getClientRects().length > 0
    }

    const focusNextFocusableElement = (attempt = 0) => {
      nextTick(() => {
        const triggerElement = document.getElementById(triggerId)
        const focusableSelector = [
          "button",
          "[href]",
          "input",
          "select",
          "textarea",
          "[tabindex]:not([tabindex='-1'])"
        ].join(",")

        const focusableElements = Array.from(
          document.querySelectorAll(focusableSelector)
        ).filter((element) => isElementFocusable(element))

        const triggerIndex = triggerElement
          ? focusableElements.indexOf(triggerElement)
          : -1
        const nextFocusable =
          triggerIndex >= 0 ? focusableElements[triggerIndex + 1] : null

        if (!nextFocusable) {
          focusDropdownRoot()
          return
        }

        nextFocusable.focus()

        const shouldRetry =
          (!document.activeElement ||
            document.activeElement === document.body ||
            document.activeElement !== nextFocusable) &&
          attempt < 3
        if (shouldRetry) {
          window.setTimeout(() => {
            focusNextFocusableElement(attempt + 1)
          }, 40)
        }
      })
    }

    const closeDropdown = ({ restoreFocus = false } = {}) => {
      state.isVisible = false
      if (restoreFocus) {
        focusDropdownRoot()
      }
    }

    const toggleDropdown = () => {
      state.isVisible = !state.isVisible
      if (state.isVisible) {
        const idx = props.options.findIndex((item) => isItemSelected(item))
        state.highlightIndex = idx >= 0 ? idx : 0
        nextTick(() => {
          const el = document.getElementById(listboxId)
          el?.focus?.()
        })
      }
    }

    const selectActive = (item) => {
      state.selectedOption = item
      closeDropdown()
      emit("itemClick", item)
      focusNextFocusableElement()
    }

    const onDocumentClick = (e) => {
      const target =
        e.target instanceof Element ? e.target : e.target?.parentElement
      if (!target || !target.closest(".v3custom-dropdown")) {
        closeDropdown()
      }
    }

    const onTriggerKeydown = (event) => {
      const key = event.key
      if (key === "ArrowDown" || key === "Enter" || key === " ") {
        event.preventDefault()
        if (!state.isVisible) {
          state.isVisible = true
          const idx = props.options.findIndex((item) => isItemSelected(item))
          state.highlightIndex = idx >= 0 ? idx : 0
          nextTick(() => document.getElementById(listboxId)?.focus())
        } else if (key === "Enter" || key === " ") {
          const item = props.options[state.highlightIndex]
          if (item) selectActive(item)
        }
      } else if (key === "Escape") {
        closeDropdown({ restoreFocus: true })
      }
    }

    const onListboxKeydown = (event) => {
      const max = props.options.length - 1
      if (max < 0) return

      const key = event.key
      if (key === "ArrowDown") {
        event.preventDefault()
        state.highlightIndex =
          state.highlightIndex >= max ? 0 : state.highlightIndex + 1
      } else if (key === "ArrowUp") {
        event.preventDefault()
        state.highlightIndex =
          state.highlightIndex <= 0 ? max : state.highlightIndex - 1
      } else if (key === "Home") {
        event.preventDefault()
        state.highlightIndex = 0
      } else if (key === "End") {
        event.preventDefault()
        state.highlightIndex = max
      } else if (key === "Enter" || key === " ") {
        event.preventDefault()
        const item = props.options[state.highlightIndex]
        if (item) selectActive(item)
      } else if (key === "Escape") {
        event.preventDefault()
        closeDropdown({ restoreFocus: true })
      } else if (key === "Tab") {
        closeDropdown()
      }
    }

    onMounted(() => {
      state.selectedOption = props.options.length ? props.options[0] : null
      document.addEventListener("click", onDocumentClick)
    })

    onUnmounted(() => {
      document.removeEventListener("click", onDocumentClick)
    })

    return {
      dropdownRootRef,
      state,
      triggerId,
      listboxId,
      listboxActiveDescendantId,
      toggleDropdown,
      selectActive,
      label,
      iconClass,
      buttonAriaLabel,
      isItemSelected,
      onTriggerKeydown,
      onListboxKeydown
    }
  }
}
</script>

<style lang="scss" scoped>
@import "src/assets/v3scss/dropdown/custom-dropdown.scss";
</style>
